import { createPortal } from "react-dom";
import { useEffect, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCurrentMetricsSummary, metricsIdentity, metricsScope, subscribeMetricsIdentity, type CurrentMetricsSummary } from "@rekonas/data-access";

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}
export function metricsSummaryValues(result: Record<string, unknown> | null): Array<[string, string]> {
  const statistics=object(object(result?.hypnogram).sleep_statistics);
  return ([ ["tst", "Total sleep", "min"], ["se", "Sleep efficiency", "%"], ["sol", "Sleep onset", "min"],
    ["waso", "Wake after sleep onset", "min"], ["pct_n1", "N1", "%"], ["pct_n2", "N2", "%"],
    ["pct_n3", "N3", "%"], ["pct_rem", "REM", "%"] ] as const).map(([key,label,unit]) => {
    const value=statistics[key];
    return [label, typeof value === "number" && Number.isFinite(value) && value >= 0
      ? unit === "%" ? `${value.toFixed(1)}%` : value >= 60 ? `${Math.floor(value / 60)}h ${(value % 60).toFixed(1)}m` : `${value.toFixed(1)} min`
      : "Not available"];
  });
}
export function RecordingMetricsSummary({ recordingRef, dataVersion, stageRun, informationTarget }: {
  recordingRef: string; dataVersion: string; stageRun: string | null; informationTarget?: HTMLElement | null;
}) {
  const identity=useSyncExternalStore(subscribeMetricsIdentity, metricsIdentity, metricsIdentity);
  const client=useQueryClient();
  const key=[...metricsScope("",recordingRef), "current-summary", dataVersion, stageRun];
  const query=useQuery({ queryKey:key, queryFn:({signal}) => getCurrentMetricsSummary(recordingRef,signal),
    retry:false, staleTime:30_000, gcTime:0, refetchOnWindowFocus:true });
  useEffect(() => () => { void client.cancelQueries({queryKey:key}); client.removeQueries({queryKey:key}); }, [client,identity,recordingRef,dataVersion,stageRun]);
  const data: CurrentMetricsSummary | undefined=query.isError ? undefined : query.data;
  const binding=data?.readiness.binding;
  const coherent=Boolean(data?.readiness.usable && binding && binding.data_version === dataVersion &&
    (binding.stage_run_ref || null) === stageRun);
  const values=coherent ? metricsSummaryValues(data!.result) : [];
  const qualification=data ? data.readiness.source === "manual_import" ? "Manual import · UNVERIFIED"
    : data.readiness.state === "placeholder" ? "Placeholder · no measurements"
    : data.readiness.usable ? null : "No selected usable context" : null;
  const usable=values.some(([,value]) => value !== "Not available");
  return <section className="recording-metrics-summary" aria-label="Recording metrics summary">
    <div className="recording-metrics-heading"><strong>Sleep metrics</strong></div>
    {qualification && <p>{qualification}</p>}
    {query.isPending ? <p>Loading metrics…</p> : query.isError ? <p>Metrics unavailable. Signals remain available.</p> : !usable ? <p>No usable metrics available for the current recording and stages.</p> : <>
      <dl>{values.map(([label,value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
    </>}
    {usable && informationTarget && createPortal(<section aria-label="Metrics source and status"><h2>Metrics source and status</h2>{data?.readiness.source !== "manual_import" && <p>Computed sleep statistics · not scientifically verified</p>}<p>Current selected context · revision {data?.readiness.selection_revision}</p>
        <p>{data?.readiness.run_ref} · {data?.readiness.sha256}</p><p>Stage binding: {stageRun || "Not available"} · {dataVersion}</p>
        <p>{data?.readiness.source === "manual_import" ? "Recording binding verified; imported content unverified." : "neoma.hypnogram-statistics/1 · durations in minutes; proportions in percent. REM latency, if inspected, starts at the aligned epoch-grid origin."}</p>
    </section>, informationTarget)}
  </section>;
}
